'use client';

import { c as _c } from "react/compiler-runtime";
import { globalShortcut } from '@todesktop/client-core';
import { useEffect, useEffectEvent } from 'react';
import { clientLogger } from '@/lib/client-logger';
import { revealDesktopAppWindow } from '@/lib/desktop-app-window';

/**
 * Control+Option+C on macOS and Win+Alt+C elsewhere, as an Electron accelerator. Windows and Linux
 * layouts often type characters such as "ć" with Ctrl+Alt, which a global Ctrl+Alt+C would block.
 */
const desktopHotkey = () => navigator.platform.includes('Mac') ? 'Control+Alt+C' : 'Super+Alt+C';
async function registerDesktopHotkey(accelerator: string, onPress: () => void): Promise<void> {
  try {
    await globalShortcut.register(accelerator, onPress);
    // ToDesktop resolves even when another app holds the keys, so ask whether cubic got them.
    if (!(await globalShortcut.isRegistered(accelerator))) {
      clientLogger.warn(`Another app holds ${accelerator}, so the cubic hotkey does nothing`);
    }
  } catch (error) {
    clientLogger.error('Failed to register the desktop hotkey', error);
  }
}

/**
 * Brings the desktop app forward and calls `onPress` when the user presses the desktop hotkey, even
 * in another app. Mount it only in the desktop app's main window.
 */
export function DesktopHotkey(t0) {
  const $ = _c(5);
  const {
    onPress
  } = t0;
  let t1;
  if ($[0] !== onPress) {
    t1 = () => {
      onPress();
      revealDesktopAppWindow().catch(_temp);
    };
    $[0] = onPress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const press = useEffectEvent(t1);
  let t2;
  if ($[2] !== press) {
    t2 = () => {
      const accelerator = desktopHotkey();
      registerDesktopHotkey(accelerator, () => press());
      return () => {
        globalShortcut.unregister(accelerator).catch(_temp2);
      };
    };
    $[2] = press;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  return null;
}
function _temp2() {}
function _temp(error) {
  clientLogger.error("Failed to reveal the desktop app for its hotkey", error);
}